import MedicamentAutocomplete from "../../../../components/medicament/MedicamentAutocomplete";
import {
    CHRONIC_REASON_OPTIONS,
    chronicProductName,
    existingControleChronicProductIds,
    isChronicBillingEventBilled,
    parseChronicDescription,
} from "../../../../utils/chronicBilling";
import { formatDate, formatDateTime, formatPatientName } from "../controleFormat";
import { CHARTER_THEME } from "../controleTheme";
import {
    controleChronicFieldStyle,
    controleChronicHistoryCommentStyle,
    controleChronicHistoryReasonStyle,
    controleChronicHistoryRowStyle,
    controleChronicInlineErrorStyle,
    controleChronicMetaStyle,
    controleChronicModalFormStyle,
    controleChronicProductBadgeStyle,
    controleChronicProductButtonStyle,
    controleChronicProductsListStyle,
    controleChronicProductsPanelStyle,
} from "../styles/chronicStyles";
import { buttonBase, controleModalStyle, modalCloseButtonStyle } from "../styles/layoutStyles";
import { controlePatientPdaEmptyStyle } from "../styles/patientStyles";
import ModalBackdrop from "../../../../components/ModalBackdrop";

// Fenêtres de la facturation chronique, communes aux écrans Contrôle et Contrôle Bis.
// idPrefix garde des identifiants de titre distincts par écran ; overlayStyle permet d'empiler la fenêtre sur une autre.

function ChronicProductChoice({ chronic, product, alreadyInHistory }) {
    const selected = chronic.selectedProductIds.includes(product.id);
    const badge = alreadyInHistory ? "Déjà dans l'historique" : product.billingValidated ? "Validé" : "À valider";
    return (
        <button
            type="button"
            onClick={() => chronic.toggleProduct(product.id)}
            disabled={chronic.saving || alreadyInHistory}
            style={controleChronicProductButtonStyle(selected, alreadyInHistory)}
        >
            <span style={{ minWidth: 0 }}>
                <strong style={{ overflowWrap: "anywhere" }}>{chronicProductName(product)}</strong>
                <small style={controleChronicMetaStyle}>
                    {product.commandeId ? `Commande #${product.commandeId}` : "Commande non renseignée"}
                    {product.quantite ? ` · Quantité ${product.quantite}` : ""}
                </small>
            </span>
            <span style={controleChronicProductBadgeStyle(Boolean(product.billingValidated), alreadyInHistory)}>
                {badge}
            </span>
        </button>
    );
}

// Traitements du patient pouvant être ajoutés à la facturation chronique, page par page.
function ChronicProductPicker({ chronic, patient }) {
    const existingIds = existingControleChronicProductIds(patient);
    return (
        <div style={controleChronicProductsPanelStyle}>
            {chronic.productsLoading ? (
                <div style={controlePatientPdaEmptyStyle}>Chargement des produits...</div>
            ) : chronic.products.length === 0 ? (
                <div style={controlePatientPdaEmptyStyle}>Aucun produit rattaché à ce patient.</div>
            ) : (
                <div style={controleChronicProductsListStyle}>
                    {chronic.products.map(product => (
                        <ChronicProductChoice
                            key={product.id}
                            chronic={chronic}
                            product={product}
                            alreadyInHistory={existingIds.has(String(product.id))}
                        />
                    ))}
                    {chronic.productsPage + 1 < chronic.productsTotalPages && (
                        <button
                            type="button"
                            onClick={() => chronic.loadProducts(patient.patientId, chronic.productsPage + 1)}
                            disabled={chronic.productsLoadingMore}
                            style={{ ...buttonBase, background: "white", color: "#334155" }}
                        >
                            {chronic.productsLoadingMore ? "Chargement..." : "Charger plus de produits"}
                        </button>
                    )}
                </div>
            )}
            {chronic.productsError && <div style={controleChronicInlineErrorStyle}>{chronic.productsError}</div>}
        </div>
    );
}

export function ChronicProductModal({ chronic, patient, overlayStyle, idPrefix }) {
    const { inputStyle, actionButtonStyle } = CHARTER_THEME;
    if (!chronic.showModal || !patient) return null;
    return (
        <div style={overlayStyle}>
            <ModalBackdrop onClose={() => chronic.setShowModal(false)} />
            <div role="dialog" aria-modal="true" aria-labelledby={`${idPrefix}-chronic-title`} style={{ ...controleModalStyle, maxWidth: "780px", position: "relative", zIndex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" }}>
                    <div style={{ minWidth: 0 }}>
                        <h2 id={`${idPrefix}-chronic-title`} style={{ margin: 0, color: "#0f172a", fontSize: "18px", fontWeight: 950 }}>
                            Ajouter un produit
                        </h2>
                        <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: "13px", overflowWrap: "anywhere" }}>
                            {formatPatientName(patient)}
                        </p>
                    </div>
                    <button type="button" onClick={() => chronic.setShowModal(false)} style={modalCloseButtonStyle}>×</button>
                </div>
                <div style={controleChronicModalFormStyle}>
                    <label style={controleChronicFieldStyle}>
                        Nom du produit
                        <MedicamentAutocomplete
                            value={chronic.name}
                            onChange={chronic.setName}
                            inputStyle={inputStyle}
                        />
                    </label>
                    <label style={controleChronicFieldStyle}>
                        <span>Quantité</span>
                        <input
                            value={chronic.quantity}
                            onChange={event => chronic.setQuantity(event.target.value)}
                            placeholder="Optionnel"
                            disabled={chronic.saving}
                            style={inputStyle}
                        />
                    </label>
                    <label style={controleChronicFieldStyle}>
                        <span>Motif</span>
                        <select
                            value={chronic.reason}
                            onChange={event => chronic.setReason(event.target.value)}
                            disabled={chronic.saving}
                            style={inputStyle}
                        >
                            <option value="">—</option>
                            {CHRONIC_REASON_OPTIONS.filter(Boolean).map(reason => (
                                <option key={reason} value={reason}>{reason}</option>
                            ))}
                        </select>
                    </label>
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "end" }}>
                        <button
                            type="button"
                            onClick={() => chronic.addManual(patient)}
                            disabled={chronic.saving || !chronic.name.trim()}
                            style={{
                                ...actionButtonStyle,
                                opacity: chronic.saving || !chronic.name.trim() ? 0.55 : 1,
                                cursor: chronic.saving || !chronic.name.trim() ? "not-allowed" : "pointer",
                            }}
                        >
                            {chronic.saving ? "Ajout..." : "Ajouter le produit"}
                        </button>
                    </div>
                </div>
                <ChronicProductPicker chronic={chronic} patient={patient} />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
                    <button type="button" onClick={() => chronic.setShowModal(false)} disabled={chronic.saving} style={{ ...buttonBase, background: "white", color: "#334155" }}>
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={() => chronic.addSelected(patient)}
                        disabled={chronic.saving || chronic.selectedProductIds.length === 0}
                        style={{
                            ...actionButtonStyle,
                            opacity: chronic.saving || chronic.selectedProductIds.length === 0 ? 0.55 : 1,
                            cursor: chronic.saving || chronic.selectedProductIds.length === 0 ? "not-allowed" : "pointer",
                        }}
                    >
                        {chronic.saving ? "Ajout..." : "Ajouter ces produits"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function ChronicDeleteConfirmModal({ chronic, overlayStyle }) {
    const candidate = chronic.deleteCandidate;
    if (!candidate) return null;
    const info = parseChronicDescription(candidate.event?.description);
    return (
        <div style={overlayStyle}>
            <ModalBackdrop onClose={() => chronic.setDeleteCandidate(null)} />
            <div role="alertdialog" aria-modal="true" aria-labelledby="delete-chronic-title" style={{ ...controleModalStyle, maxWidth: "560px", position: "relative", zIndex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" }}>
                    <div style={{ minWidth: 0 }}>
                        <h2 id="delete-chronic-title" style={{ margin: 0, color: "#0f172a", fontSize: "18px", fontWeight: 950 }}>
                            Retirer de la facturation ?
                        </h2>
                        <p style={{ margin: "8px 0 0", color: "#475569", fontSize: "13px", lineHeight: 1.45, overflowWrap: "anywhere" }}>
                            {info.productInfo || "Produit sans information"}
                        </p>
                    </div>
                    <button type="button" onClick={() => chronic.setDeleteCandidate(null)} style={modalCloseButtonStyle}>×</button>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
                    <button type="button" onClick={() => chronic.setDeleteCandidate(null)} style={{ ...buttonBase, background: "white", color: "#334155" }}>
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={chronic.confirmDelete}
                        disabled={chronic.savingId === candidate.event?.id}
                        style={{
                            ...buttonBase,
                            background: "#dc2626",
                            borderColor: "#dc2626",
                            color: "white",
                            fontWeight: 900,
                            opacity: chronic.savingId === candidate.event?.id ? 0.65 : 1,
                        }}
                    >
                        {chronic.savingId === candidate.event?.id ? "Suppression..." : "Retirer de la facturation"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function ChronicBillConfirmModal({ chronic, overlayStyle }) {
    const { actionButtonStyle } = CHARTER_THEME;
    const candidate = chronic.billCandidate;
    if (!candidate) return null;
    const info = parseChronicDescription(candidate.event?.description);
    return (
        <div style={overlayStyle}>
            <ModalBackdrop onClose={() => chronic.setBillCandidate(null)} />
            <div role="alertdialog" aria-modal="true" aria-labelledby="bill-chronic-title" style={{ ...controleModalStyle, maxWidth: "560px", position: "relative", zIndex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" }}>
                    <div style={{ minWidth: 0 }}>
                        <h2 id="bill-chronic-title" style={{ margin: 0, color: "#0f172a", fontSize: "18px", fontWeight: 950 }}>
                            Marquer comme facturé ?
                        </h2>
                        <p style={{ margin: "8px 0 0", color: "#475569", fontSize: "13px", lineHeight: 1.45, overflowWrap: "anywhere" }}>
                            {info.productInfo || "Produit sans information"}
                        </p>
                    </div>
                    <button type="button" onClick={() => chronic.setBillCandidate(null)} style={modalCloseButtonStyle}>×</button>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
                    <button type="button" onClick={() => chronic.setBillCandidate(null)} style={{ ...buttonBase, background: "white", color: "#334155" }}>
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={chronic.confirmBill}
                        disabled={chronic.savingId === candidate.event?.id}
                        style={{
                            ...actionButtonStyle,
                            opacity: chronic.savingId === candidate.event?.id ? 0.65 : 1,
                        }}
                    >
                        {chronic.savingId === candidate.event?.id ? "Enregistrement..." : "Valider"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function ChronicHistoryModal({ chronic, overlayStyle, idPrefix }) {
    if (!chronic.historyPatient) return null;
    const events = [...(chronic.historyPatient.billingEvents || [])]
        .filter(event => event.type === "CHRONIC")
        .filter(isChronicBillingEventBilled)
        .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || (b.id || 0) - (a.id || 0));

    return (
        <div style={overlayStyle}>
            <ModalBackdrop onClose={() => chronic.setHistoryPatient(null)} />
            <div role="dialog" aria-modal="true" aria-labelledby={`${idPrefix}-chronic-history-title`} style={{ ...controleModalStyle, maxWidth: "760px", position: "relative", zIndex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" }}>
                    <div style={{ minWidth: 0 }}>
                        <h2 id={`${idPrefix}-chronic-history-title`} style={{ margin: 0, color: "#0f172a", fontSize: "18px", fontWeight: 950 }}>
                            Historique facturation chronique
                        </h2>
                        <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: "13px", overflowWrap: "anywhere" }}>
                            {formatPatientName(chronic.historyPatient)}
                        </p>
                    </div>
                    <button type="button" onClick={() => chronic.setHistoryPatient(null)} style={modalCloseButtonStyle}>×</button>
                </div>
                <div style={{ maxHeight: "520px", overflowY: "auto", display: "grid", gap: "8px" }}>
                    {events.length === 0 ? (
                        <div style={controlePatientPdaEmptyStyle}>Aucun produit facturé dans l'historique.</div>
                    ) : events.map(event => {
                        const info = parseChronicDescription(event.description);
                        return (
                            <div key={event.id} style={controleChronicHistoryRowStyle}>
                                <strong style={{ color: "#1e293b", fontSize: "13px", overflowWrap: "anywhere" }}>
                                    {info.productInfo || "Produit sans information"}
                                </strong>
                                <small style={controleChronicMetaStyle}>
                                    Date produit : {formatDate(event.date)}
                                    {" · "}
                                    Mis en facturation le {formatDateTime(event.createdAt) || "date inconnue"}
                                    {event.createdBy ? ` par ${event.createdBy}` : ""}
                                </small>
                                {info.reason && <small style={controleChronicHistoryReasonStyle}>Motif : {info.reason}</small>}
                                {info.comment && <small style={controleChronicHistoryCommentStyle}>Commentaire : {info.comment}</small>}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
